import { SyntheticEvent, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import {
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';

import { CfsApi, MasterNameApi, MasterVehicleApi } from '@/api';
import { IcClock, IcMarkerPin, IcPencil, IcPopUp } from '@/assets/images';
import {
  MasterNameAutocomplete,
  NoResultItem,
  Page,
  PageHeader,
  RenderFormField,
} from '@/components';
import { DATE_TIME_FORMAT } from '@/config';
import { useCFSContext } from '@/hooks';
import { useMasterNamesContext } from '@/layouts/TabLayout/MasterNames';
import { useMasterVehicleContext } from '@/layouts/TabLayout/MasterVehicles';
import { getActiveLicensePlate } from '@/layouts/TabLayout/MasterVehicles/licensePlate.utils';
import {
  ICFSForm,
  ICFSInvolvement,
  IMasterName,
  IMasterNamePerson,
  IMasterVehicle,
  MasterNameType,
} from '@/models';
import { cfsDefaultValues } from '@/pages/CFS/Edit/data';
import { getAddressCommonString, getAddressInfoLocation } from '@/services';
import { ConfirmTypes, useToastStore } from '@/store';
import { colors } from '@/theme/variables';
import { formatPersonName, openNewWindow, parsePhoneNumber } from '@/utils';

import AddPersonPanel from '../components/AddPersonPanel';
import AddVehiclePanel from '../components/AddVehiclePanel';
import CarDetailsCard from '../components/CarDetailsCard';
import { default as PersonDetailsCard } from '../components/PersonDetailsCard';
import RelationsList from '../components/RelationsList';

type CarStopCfsTabProps = {
  handleBack: () => void;
};

export type TabType = 'details' | 'relations-cfs' | 'relations-cases';

const CarStopCfsTab = ({ handleBack }: CarStopCfsTabProps) => {
  const { cfs, updateCFS, ablyCfsChannel, mapBounds } = useCFSContext();
  const { updateToast } = useToastStore();
  const [selectedTab, setSelectedTab] = useState<TabType>('details');
  const [linkedPersons, setLinkedPersons] = useState<IMasterName[]>([]);
  const [linkedVehicles, setLinkedVehicles] = useState<IMasterVehicle[]>([]);

  const [searchText, setSearchText] = useState('');
  const [searchedVehicles, setSearchedVehicles] = useState<IMasterVehicle[]>(
    [],
  );
  const [isVehicleAutocompleteOpen, setIsVehicleAutocompleteOpen] =
    useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddPersonPanel, setShowAddPersonPanel] = useState(false);
  const [showAddVehiclePanel, setShowAddVehiclePanel] = useState(false);
  const [personSearchKey, setPersonSearchKey] = useState(0);
  const [vehicleSearchKey, setVehicleSearchKey] = useState(0);

  const formMethods = useForm<ICFSForm>({
    defaultValues: cfsDefaultValues,
    mode: 'onChange',
  });

  const reporterTime = formMethods.watch('reporter.reportTime');

  const { handleAddLinkedName, handleDeleteLinkedName } =
    useMasterNamesContext();
  const { handleAddLinkedVehicle, handleDeleteLinkedVehicle } =
    useMasterVehicleContext();

  const handleChangeReporterTime = async () => {
    const newDateTime = formMethods.getValues('reporter.reportTime');
    try {
      if (cfs)
        await CfsApi.update(String(cfs?._id), {
          reporter: { reportTime: newDateTime },
        }).then((res) => {
          updateCFS({
            ...cfs,
            reporter: { ...res.data.reporter },
          });
          ablyCfsChannel?.publish('cfsForm', {
            reportTime: res.data.reporter.reportTime,
          });
          updateToast({
            type: ConfirmTypes.SUCCESS,
            open: true,
            message: 'Reporter time updated',
          });
        });
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const onUpdateAddressInfo = () => {
    const newAddress = formMethods.getValues('addressInfo.address');
    CfsApi.updateAddressInfo(cfs?._id as string, newAddress?._id)
      .then((res) => {
        ablyCfsChannel?.publish('cfsForm', {
          addressInfo: {
            ...cfsDefaultValues.addressInfo,
            ...res.data.addressInfo,
          },
        });
        if (cfs)
          updateCFS({
            ...cfs,
            addressInfo: res.data.addressInfo,
          });
      })
      .catch((err: any) => {
        updateToast({ open: true, message: err.message });
      });
  };

  const handlePersonChange = async (newValue: IMasterName) => {
    if (!newValue._id) return;
    if (!cfs?._id) return;
    if (linkedPersons.some((p) => p._id === newValue._id)) return;

    const data = {
      relationship: {
        behavior: 'N/A',
        involvement: [],
      } as ICFSInvolvement,
    };

    handleAddLinkedName(newValue._id, data);
    const mastername = await MasterNameApi.getMasterName(newValue._id);
    setLinkedPersons((prev) => [...prev, mastername.data]);
  };

  const handleRemovePerson = async (personId: string) => {
    if (handleDeleteLinkedName) {
      await handleDeleteLinkedName(personId);
    }
    setLinkedPersons((prev) => prev.filter((p) => p._id !== personId));
  };

  const updateAddedVehicles = async (vehicleId: string) => {
    try {
      await handleAddLinkedVehicle(vehicleId);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const handleVehicleChange = async (newValue: IMasterVehicle) => {
    if (!newValue._id) return;
    if (linkedVehicles.some((v) => v._id === newValue._id)) return;

    updateAddedVehicles(newValue._id);
    const masterVehicle = await MasterVehicleApi.getMasterVehicle(newValue._id);
    setLinkedVehicles((prev) => [...prev, masterVehicle.data]);
    setSearchText('');
    setIsVehicleAutocompleteOpen(false);
  };

  const handleRemoveVehicle = async (vehicleId: string) => {
    if (handleDeleteLinkedVehicle) {
      await handleDeleteLinkedVehicle(vehicleId);
    }
    setLinkedVehicles((prev) => prev.filter((v) => v._id !== vehicleId));
  };

  useEffect(() => {
    if (cfs?._id) {
      const data: ICFSForm = {
        number: cfs.number,
        severity: cfs.severity,
        shortDescription: cfs.shortDescription,
        incidentCodes: cfs.incidentCodes.map((code) => String(code._id)),
        addressInfo: {
          ...cfsDefaultValues.addressInfo,
          ...cfs.addressInfo,
          instructions: cfs.addressInfo?.instructions || '',
        },
        reporter: {
          ...cfs.reporter,
          contactPhoneNumber: parsePhoneNumber(cfs.reporter.contactPhoneNumber),
        },
      };
      formMethods.reset(data);
    }
  }, [cfs?._id]);

  useEffect(() => {
    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        const params = {
          search: searchText,
          limit: 50,
        };
        const { data } = await MasterVehicleApi.getMasterVehicles(params);
        if (!cancelled) {
          setSearchedVehicles(data.docs);
        }
      } catch (err: any) {
        if (!cancelled) {
          updateToast({ open: true, message: err });
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchText, vehicleSearchKey]);

  const handleInputChange = (_: SyntheticEvent, v: string) => {
    setSearchText(v);
  };

  const renderHeader = (tab: TabType) => {
    switch (tab) {
      case 'details': {
        return (
          <Stack direction="row" spacing={2} padding={2} alignItems="center">
            <ButtonBase
              onClick={() => setIsEditing(!isEditing)}
              sx={{ padding: 1 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                columnGap={1}
                marginRight={2}
              >
                {getAddressInfoLocation(cfs?.addressInfo) && (
                  <>
                    <IcMarkerPin color={colors.grey[80]} height={20} />
                    <Typography color={colors.grey[80]} variant="body2">
                      {cfs?.addressInfo?.address
                        ? getAddressCommonString(cfs?.addressInfo?.address)
                        : getAddressInfoLocation(cfs?.addressInfo)}
                    </Typography>
                  </>
                )}
              </Stack>
              <Stack direction="row" alignItems="center" columnGap={1}>
                <IcClock color={colors.grey[80]} height={20} />
                <Typography color={colors.grey[80]} variant="body2">
                  {moment(reporterTime).format(DATE_TIME_FORMAT)}
                </Typography>
                <IcPencil />
              </Stack>
            </ButtonBase>
          </Stack>
        );
      }
      case 'relations-cases': {
        return <></>;
      }
      case 'relations-cfs': {
        return <></>;
      }
    }
  };

  const renderContent = (tab: TabType) => {
    switch (tab) {
      case 'details': {
        return (
          <>
            <Box display="flex" flex={1} flexDirection="column" gap={2}>
              <Box minHeight={56} display="flex" alignItems="flex-start">
                <MasterNameAutocomplete
                  key={personSearchKey}
                  excludedMasterNames={linkedPersons}
                  onChange={handlePersonChange}
                  filterByType={MasterNameType.PERSON}
                  noResults={
                    <NoResultItem
                      flexDirection="row"
                      alignItems="center"
                      sx={{ ml: -1 }}
                    >
                      <Button
                        onClick={() => setShowAddPersonPanel(true)}
                        variant="text"
                        color="error"
                      >
                        + Add new
                      </Button>
                    </NoResultItem>
                  }
                />
              </Box>
              {linkedPersons.map((person) => (
                <PersonDetailsCard
                  key={person._id}
                  data={person as IMasterNamePerson}
                  setSelectedTab={setSelectedTab}
                  onRemove={() => handleRemovePerson(person._id as string)}
                />
              ))}
            </Box>
            <Box display="flex" flex={1} flexDirection="column" gap={2}>
              <Box minHeight={56} display="flex" alignItems="flex-start">
                <Autocomplete
                  key={vehicleSearchKey}
                  fullWidth
                  open={isVehicleAutocompleteOpen}
                  inputValue={searchText}
                  onOpen={() => setIsVehicleAutocompleteOpen(true)}
                  onClose={() => setIsVehicleAutocompleteOpen(false)}
                  onInputChange={handleInputChange}
                  options={searchedVehicles}
                  filterOptions={(options) =>
                    options.filter(
                      (opt) => !linkedVehicles.some((v) => v._id === opt._id),
                    )
                  }
                  noOptionsText={
                    <NoResultItem
                      flexDirection="row"
                      alignItems="center"
                      sx={{ ml: -1 }}
                    >
                      <Button
                        onClick={() => setShowAddVehiclePanel(true)}
                        variant="text"
                        color="error"
                      >
                        + Add new
                      </Button>
                    </NoResultItem>
                  }
                  onChange={(_, v) => {
                    if (v) handleVehicleChange(v);
                  }}
                  getOptionLabel={() => ''}
                  renderOption={(props, option) => {
                    const year = option.year || '';
                    const make =
                      typeof option.make === 'object' &&
                      option.make !== null &&
                      'name' in option.make
                        ? (option.make as { name: string }).name
                        : option.make || '';
                    const model =
                      typeof option.model === 'object' &&
                      option.model !== null &&
                      'name' in option.model
                        ? (option.model as { name: string }).name
                        : option.model || '';
                    const plate = getActiveLicensePlate(option)?.number || '';
                    const color =
                      typeof option.color === 'object' &&
                      option.color !== null &&
                      'name' in option.color
                        ? (option.color as { name: string }).name
                        : (option.color as unknown as string) || '';
                    const ownerName = formatPersonName(option.owner);
                    const secondaryParts = [plate, color, ownerName].filter(
                      Boolean,
                    );

                    return (
                      <Box
                        component="li"
                        {...props}
                        key={option._id}
                        onClick={() => {
                          if (option) handleVehicleChange(option);
                        }}
                        sx={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          borderBottom: `1px solid ${colors.grey[10]}`,
                          margin: 0,
                          minHeight: '54px',
                          padding: '8px 12px',
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Stack
                            spacing={0.25}
                            sx={{ flex: 1, minWidth: 0 }}
                            pr={1.5}
                          >
                            <Typography
                              fontWeight={600}
                              sx={{ fontSize: 15, lineHeight: 1.2 }}
                            >
                              {[year, make, model].filter(Boolean).join(' ')}
                            </Typography>
                            {secondaryParts.length > 0 && (
                              <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="center"
                              >
                                <DirectionsCarOutlinedIcon
                                  sx={{
                                    color: colors.grey[80],
                                    width: 15,
                                    height: 15,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: colors.grey[80],
                                    fontSize: 13,
                                    lineHeight: 1.15,
                                  }}
                                >
                                  {secondaryParts.join(' \u00B7 ')}
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            gap="8px"
                            paddingRight="2px"
                            alignItems="center"
                            flexShrink={0}
                          >
                            <Button
                              type="button"
                              variant="outlined"
                              color="inherit"
                              size="small"
                              sx={{ minWidth: 78, height: 36 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (option) handleVehicleChange(option);
                              }}
                            >
                              Add
                            </Button>
                            <IconButton
                              aria-label="open-vehicle"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (option._id) {
                                  openNewWindow(
                                    `/master-index/vehicles/${option._id}/information`,
                                    option._id,
                                  );
                                }
                              }}
                            >
                              <IcPopUp />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      value={searchText}
                      placeholder="Search vehicles..."
                      fullWidth
                    />
                  )}
                  clearOnBlur={false}
                />
              </Box>
              {linkedVehicles.map((vehicle) => (
                <CarDetailsCard
                  key={vehicle._id}
                  data={vehicle as IMasterVehicle}
                  setSelectedTab={setSelectedTab}
                  onRemove={() => handleRemoveVehicle(vehicle._id as string)}
                />
              ))}
            </Box>
          </>
        );
      }
      case 'relations-cfs': {
        return (
          <RelationsList
            type="CFS"
            currentMastername={linkedPersons[0] ?? null}
          />
        );
      }
      case 'relations-cases': {
        return (
          <RelationsList
            type="Cases"
            currentMastername={linkedPersons[0] ?? null}
          />
        );
      }
    }
  };

  const onBack = () => {
    if (selectedTab === 'details') {
      handleBack();
    } else {
      setSelectedTab('details');
    }
  };

  const getTitle = () => {
    switch (selectedTab) {
      case 'details':
        return 'Create CFS - Car Stop';
      case 'relations-cfs':
        return 'Relations CFS';
      case 'relations-cases':
        return 'Relations Cases';
    }
  };

  if (showAddPersonPanel) {
    return (
      <AddPersonPanel
        cfsId={String(cfs?._id)}
        onClose={async () => {
          setShowAddPersonPanel(false);
          setPersonSearchKey((prev) => prev + 1);
          if (cfs?._id) {
            try {
              const { data } = await CfsApi.getCfsMasterNames(String(cfs._id));
              setLinkedPersons(
                data.map((item) => item.masterName as IMasterName),
              );
            } catch {
              // Keep existing state if fetch fails
            }
          }
        }}
      />
    );
  }

  if (showAddVehiclePanel) {
    return (
      <AddVehiclePanel
        cfsId={String(cfs?._id)}
        onClose={async () => {
          setShowAddVehiclePanel(false);
          setSearchText('');
          setVehicleSearchKey((prev) => prev + 1);
          if (cfs?._id) {
            try {
              const { data } = await CfsApi.getCfsMasterVehicles(
                String(cfs._id),
              );
              setLinkedVehicles(data.map((item) => item.masterVehicle));
            } catch {
              // Keep existing state if fetch fails
            }
          }
        }}
      />
    );
  }

  return (
    <Stack>
      <FormProvider {...formMethods}>
        <Page title={getTitle()}>
          <PageHeader
            title={getTitle()}
            onBack={onBack}
            sx={{
              paddingLeft: 2,
            }}
          >
            {renderHeader(selectedTab)}
          </PageHeader>
          {isEditing && cfs && (
            <>
              <Stack display="flex" padding={1} direction="row" spacing={2}>
                <Box flex={1} paddingLeft={2}>
                  <RenderFormField
                    label="Address"
                    name="addressInfo.address"
                    type="address"
                    locationRestriction={mapBounds}
                    handleChange={onUpdateAddressInfo}
                  />
                </Box>
                <Box flex={1} paddingRight={2} paddingLeft={1}>
                  <RenderFormField
                    label="Reporter time"
                    name="reporter.reportTime"
                    type="datetime"
                    handleChange={handleChangeReporterTime}
                  />
                </Box>
              </Stack>
              <Divider />
            </>
          )}
          <Stack
            spacing={2}
            flex={1}
            direction="row"
            padding={3}
            sx={{ overflow: 'auto' }}
          >
            {renderContent(selectedTab)}
          </Stack>
        </Page>
      </FormProvider>
    </Stack>
  );
};

export default CarStopCfsTab;
